import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Check, Copy, Globe, KeyRound, Terminal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/misc";
import { Hint } from "@/components/ui/tooltip";
import { api, ApiError, type BoardMcpConfig, type McpToken } from "@/lib/api";
import type { BoardDetail } from "@/lib/types";

/**
 * How to give a Claude Code session in another project this board — and only
 * this board.
 *
 * The dialog only shows the command, it does not run it: registering an MCP
 * server is a change to the user's Claude Code config for some other directory,
 * which is theirs to make. `bun run mcp:connect <board> --add` exists for doing
 * it from a terminal.
 */
export function ConnectClaudeDialog({
  board,
  open,
  onOpenChange,
}: {
  board: BoardDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [config, setConfig] = useState<BoardMcpConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const boardId = board?.board.id ?? null;

  useEffect(() => {
    if (!open || !boardId) return;
    setConfig(null);
    setError(null);
    api
      .boardMcp(boardId)
      .then(setConfig)
      .catch((reason) => setError(reason instanceof ApiError ? reason.message : "Could not build the config"));
  }, [open, boardId]);

  return (
    <Dialog open={open && board !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Terminal className="size-4" /> Connect Claude Code to this board
          </DialogTitle>
          <DialogDescription>
            A session in your project gets <span className="font-medium text-foreground">{board?.board.name}</span> as
            an MCP server. It can read and work its cards, and nothing on any other board: other boards are refused,
            and creating boards or registering projects is not available to it.
          </DialogDescription>
        </DialogHeader>

        {error ? <p className="text-xs text-destructive">{error}</p> : null}

        {config ? (
          <div className="space-y-4 text-[12.5px]">
            <Block
              label={
                config.projectPath ? (
                  <>
                    Run inside <span className="font-mono text-foreground">{config.projectPath}</span>
                  </>
                ) : (
                  "Run inside the project the work happens in"
                )
              }
              hint="Stored in your own Claude Code config for that directory — nothing is written into the repo."
              text={config.projectPath ? `cd ${config.projectPath} && ${config.command}` : config.command}
            />
            <Block
              label="Or commit it in the project's .mcp.json"
              hint="The paths are absolute to this machine, so this suits a repo only you work in."
              text={JSON.stringify(config.mcpJson, null, 2)}
            />
            <p className="text-[11.5px] text-muted-foreground">
              Tools show up as <span className="font-mono">mcp__{config.serverName}__…</span>. Start a new session in the
              project (or run <span className="font-mono">/mcp</span>) to pick it up.
            </p>
          </div>
        ) : error ? null : (
          <p className="text-xs text-muted-foreground">Building the config…</p>
        )}

        {boardId ? (
          <>
            <Separator />
            <HttpTokens boardId={boardId} serverName={config?.serverName ?? "board"} />
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function Block({ label, hint, text }: { label: React.ReactNode; hint: string; text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <section className="space-y-1.5">
      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium">{label}</p>
          <p className="text-[11.5px] text-muted-foreground">{hint}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? <Check /> : <Copy />} {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <pre className="max-h-56 overflow-auto whitespace-pre-wrap break-all rounded-md border border-border/60 bg-surface/60 p-2.5 font-mono text-[11px] leading-relaxed scrollbar-slim">
        {text}
      </pre>
    </section>
  );
}

/**
 * Connecting over HTTP instead: for a client that cannot run a command on this
 * machine — a Docker sandbox, another computer, a hosted agent. It gets a bearer
 * token for this board's `/mcp` endpoint. The token is shown once; the board keeps
 * only a hash of it, so a lost one is revoked and replaced, never recovered.
 */
function HttpTokens({ boardId, serverName }: { boardId: string; serverName: string }) {
  const [tokens, setTokens] = useState<McpToken[]>([]);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState<{ name: string; token: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const url = `${window.location.origin}/mcp`;

  const load = useCallback(async () => {
    const { tokens: list } = await api.mcpTokens(boardId);
    setTokens(list);
  }, [boardId]);

  useEffect(() => {
    setFresh(null);
    void load().catch((reason) => setError(reason instanceof ApiError ? reason.message : "Could not list tokens"));
  }, [load]);

  const create = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const { token, record } = await api.createMcpToken({ name: name.trim() || "HTTP client", boardId });
      setFresh({ name: record.name, token });
      setName("");
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Could not create the token");
    }
  };

  const revoke = async (token: McpToken) => {
    try {
      await api.revokeMcpToken(token.id);
      await load();
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Could not revoke the token");
    }
  };

  return (
    <section className="space-y-3 text-[12.5px]">
      <div>
        <p className="flex items-center gap-1.5 font-medium">
          <Globe className="size-3.5" /> Or connect over HTTP
        </p>
        <p className="text-[11.5px] text-muted-foreground">
          For a client that cannot run a command here: a Docker sandbox, another machine, a hosted agent. It gets a token
          for <span className="font-mono">{url}</span>, scoped to this board.
        </p>
      </div>

      <form onSubmit={create} className="flex gap-2">
        <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="What will use it (e.g. laptop, CI)" />
        <Button type="submit" variant="outline">
          <KeyRound /> Create token
        </Button>
      </form>

      {fresh ? (
        <Block
          label={`Token "${fresh.name}" — shown once`}
          hint="Copy it now: only a hash is kept, so it cannot be shown again."
          text={`claude mcp add --transport http ${serverName} ${url} --header "Authorization: Bearer ${fresh.token}"`}
        />
      ) : null}

      {tokens.length > 0 ? (
        <ul className="space-y-1">
          {tokens.map((token) => (
            <li key={token.id} className="flex items-center gap-2 text-[12px]">
              <KeyRound className="size-3.5 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate">
                {token.name}
                {token.boardId === null ? <span className="text-muted-foreground"> · every board</span> : null}
              </span>
              <span className="text-[11px] text-muted-foreground">
                {token.lastUsedAt ? `used ${new Date(token.lastUsedAt).toLocaleString()}` : "never used"}
              </span>
              <Hint label="Revoke — anything using it stops working at once">
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 text-muted-foreground hover:text-destructive"
                  onClick={() => void revoke(token)}
                  aria-label={`Revoke ${token.name}`}
                >
                  <X className="size-3.5" />
                </Button>
              </Hint>
            </li>
          ))}
        </ul>
      ) : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </section>
  );
}
